import { PlanResults } from "./PlanResults";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import {
  BATCH_ITEM_STATES,
  BATCH_ITEMS_PAGE,
  changeBatchSettings,
  controlBatch,
  listBatchItems,
  readBatch,
  retryBatchItem,
  type Batch,
  type BatchControl,
  type BatchItem,
  type BatchItemState,
  type Usage,
} from "../../api/threads/batchesApi";
import { appPaths } from "../../shared/routing/appPaths";
import ErrorNotice from "../../errors/ErrorNotice";
import ItemReferenceName from "../items/ItemReferenceName";
import { useSession } from "../../session/sessionContext";
import { formatDateTime } from "../../shared/formatting/dateTimes";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import ReferenceDisplay from "../../ui/content/ReferenceDisplay";
import ProgressBar from "../../ui/feedback/ProgressBar";
import { FORM_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../ui/styles/formStyles";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { useTabTitle } from "../../shell/workspace";

const REFRESH_MILLISECONDS = 3000;
const ACTIVE_STATES = new Set(["planned", "running", "paused"]);

/** `/batches/<batch id>`: a batch's progress, its controls and its items (FR-WF-5). */
export default function BatchPage() {
  const { t } = useTranslation();
  const { batchId = "" } = useParams();
  const loadBatch = useCallback((signal: AbortSignal) => readBatch(batchId, signal), [batchId]);
  const { state, reload, refresh } = useLoad(loadBatch);
  useTabTitle(state.status === "loaded" ? { kind: "other", name: state.value.title } : null);
  const active = state.status === "loaded" && ACTIVE_STATES.has(state.value.state);

  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(refresh, REFRESH_MILLISECONDS);
    return () => { window.clearInterval(timer); };
  }, [active, refresh]);

  return (
    <div className="mx-auto max-w-5xl space-y-4 py-4">
      <nav className="text-xs">
        <Link to={appPaths.sources()} className="app-link underline">{t("sections.sources")}</Link>
      </nav>
      <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />
      {state.status === "loaded" ? <BatchView batch={state.value} onChanged={refresh} /> : null}
    </div>
  );
}

function BatchView({ batch, onChanged }: { batch: Batch; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const { session } = useSession();
  const isStarter = session.status === "signedIn" && session.session.account.userId === batch.createdBy;
  const { state, submit } = useSubmission();
  const finished = batch.counts.done + batch.counts.failed + batch.counts.cancelled;
  const control = (action: BatchControl) => {
    submit(async () => {
      await controlBatch(batch.batchId, action);
      onChanged();
    });
  };
  const allowed: Record<BatchControl, boolean> = {
    start: batch.state === "planned",
    pause: batch.state === "running",
    resume: batch.state === "paused",
    cancel: ACTIVE_STATES.has(batch.state),
  };

  return (
    <>
      <header className="space-y-1">
        <h2 className="app-title text-xl font-bold">{batch.title}</h2>
        <p className="text-sm">
          <strong>{t(`batches.state.${batch.state}`)}</strong>
          {batch.pauseReason === null ? null : ` · ${t(`batches.pauseReason.${batch.pauseReason}`)}`}
          {batch.held ? ` · ${t("batches.held")}` : null}
          {batch.heldUntil === null ? null : ` ${t("batches.heldUntil", { time: formatDateTime(batch.heldUntil, i18n.resolvedLanguage) })}`}
          {" · "}
          {batch.connectorOnline ? t("batches.connectorOnline") : t("batches.connectorOffline")}
        </p>
        <p className="app-text-muted flex flex-wrap items-center gap-x-1 text-xs">
          {t("batches.workflow")}:{" "}
          <Link to={appPaths.workflow(batch.workflow)} className="app-link underline">
            <ItemReferenceName reference={batch.workflow} link={false} />
          </Link>
          {" · "}
          {t("batches.corpusVersion")}: <ReferenceDisplay kind="version" prefix="@" value={batch.corpusVersion} />
        </p>
      </header>
      <section className="app-card space-y-2 p-4">
        <ProgressBar value={batch.unitCount === 0 ? 0 : (100 * finished) / batch.unitCount} />
        <p className="text-sm">
          {BATCH_ITEM_STATES.filter((itemState) => batch.counts[itemState] > 0)
            .map((itemState) => `${t(`batches.itemState.${itemState}`)}: ${String(batch.counts[itemState])}`)
            .join(" · ")}
          {` · ${t("batches.ofTotal", { count: batch.unitCount })}`}
        </p>
        <p className="app-text-muted text-xs">
          {t("batches.costSoFar")}: {usageText(batch.usage, t("batches.unknown"))}
          {" · "}
          {t("batches.eta")}: {batch.estimatedRemainingSeconds === null ? t("batches.unknown") : durationText(batch.estimatedRemainingSeconds)}
          {" · "}
          {t("batches.concurrencyInForce", { count: batch.concurrency })}
        </p>
        {isStarter ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {(Object.keys(allowed) as BatchControl[]).filter((action) => allowed[action]).map((action) => (
              <AppButton key={action} variant={action === "cancel" ? "ghost" : "primary"} size="sm" disabled={state.status === "submitting"} onClick={() => { control(action); }}>
                {t(`batches.control.${action}`)}
              </AppButton>
            ))}
            {ACTIVE_STATES.has(batch.state) ? <ConcurrencyControl batch={batch} onChanged={onChanged} /> : null}
          </div>
        ) : null}
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      </section>
      {batch.chunks.length === 0 ? null : (
        <section className="space-y-1">
          <h3 className="app-title text-sm font-semibold">{t("batches.chunks")}</h3>
          <ul className="text-sm">
            {batch.chunks.map((chunk) => (
              <li key={chunk.number}>
                {t("batches.chunk", { number: chunk.number + 1, count: chunk.itemCount })} · {t(`batches.chunkState.${chunk.state}`)}
                {chunk.changeSetId === null ? null : (
                  <> · <Link to={appPaths.review(chunk.changeSetId)} className="app-link underline">{t("batches.review")}</Link></>
                )}
                {chunk.problem === null ? null : ` · ${chunk.problem.detail}`}
              </li>
            ))}
          </ul>
        </section>
      )}
      <PlanResults batch={batch} />
      <BatchItems batch={batch} isStarter={isStarter} onChanged={onChanged} />
    </>
  );
}

function ConcurrencyControl({ batch, onChanged }: { batch: Batch; onChanged: () => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(batch.concurrencySetting === null ? "" : String(batch.concurrencySetting));
  const { state, submit } = useSubmission();
  const parsed = value.trim() === "" ? null : Number.parseInt(value, 10);
  const save = () => {
    submit(async () => {
      await changeBatchSettings(batch.batchId, parsed, batch.humanSteps);
      onChanged();
    });
  };
  return (
    <span className="flex items-center gap-1 text-xs">
      <input className={`${FORM_CONTROL_CLASS} w-20`} type="number" min={1} value={value} placeholder={t("batches.start.concurrencyDefault")} aria-label={t("batches.start.concurrency")} onChange={(event) => { setValue(event.target.value); }} />
      <AppButton variant="secondary" size="sm" disabled={state.status === "submitting" || (parsed !== null && parsed < 1)} onClick={save}>{t("batches.setConcurrency")}</AppButton>
    </span>
  );
}

function BatchItems({ batch, isStarter, onChanged }: { batch: Batch; isStarter: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<BatchItemState | null>(null);
  // Pages start at an item index; `pageStarts` remembers the earlier ones for "Previous".
  const [pageStarts, setPageStarts] = useState<readonly number[]>([0]);
  const fromIndex = pageStarts[pageStarts.length - 1] ?? 0;
  const loadItems = useCallback((signal: AbortSignal) => listBatchItems(batch.batchId, fromIndex, filter, signal), [batch.batchId, fromIndex, filter]);
  const { state, refresh } = useLoad(loadItems);
  // The list follows the batch: whenever its counts move, the items are read again.
  const progressKey = BATCH_ITEM_STATES.map((itemState) => batch.counts[itemState]).join(",");
  useEffect(() => { refresh(); }, [progressKey, refresh]);
  const retry = useSubmission();
  const retryItem = (item: BatchItem) => {
    retry.submit(async () => {
      await retryBatchItem(batch.batchId, item.index);
      refresh();
      onChanged();
    });
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="app-title flex-1 text-sm font-semibold">{t("batches.items")}</h3>
        <select className={`${FORM_SELECT_CLASS} w-48`} value={filter ?? ""} aria-label={t("batches.filter")} onChange={(event) => { setFilter(event.target.value === "" ? null : (event.target.value as BatchItemState)); setPageStarts([0]); }}>
          <option value="">{t("batches.allItems")}</option>
          {BATCH_ITEM_STATES.map((itemState) => <option key={itemState} value={itemState}>{t(`batches.itemState.${itemState}`)}</option>)}
        </select>
      </div>
      {retry.state.status === "failed" ? <ErrorNotice error={retry.state.error} compact={true} /> : null}
      {state.status === "failed" ? <ErrorNotice error={state.error} onRetry={refresh} /> : null}
      {state.status === "loaded" ? (
        <table className="w-full text-left text-xs">
          <thead>
            <tr>
              <th className="app-table-head px-2 py-1">{t("batches.column.unit")}</th>
              <th className="app-table-head px-2 py-1">{t("batches.column.state")}</th>
              <th className="app-table-head px-2 py-1">{t("batches.column.attempt")}</th>
              <th className="app-table-head px-2 py-1">{t("batches.column.usage")}</th>
              <th className="app-table-head px-2 py-1" />
            </tr>
          </thead>
          <tbody>
            {state.value.map((item) => (
              <tr key={item.index} className="align-top">
                <td className="px-2 py-1">{item.segmentPath === null ? item.documentKey : `${item.documentKey} #${item.segmentPath}`}</td>
                <td className="px-2 py-1">
                  {t(`batches.itemState.${item.state}`)}
                  {item.problem === null ? null : <span className="app-text-muted block">{item.problem.code}: {item.problem.detail}</span>}
                </td>
                <td className="px-2 py-1">{item.attempt}</td>
                <td className="px-2 py-1">{usageText(item.usage, "–")}</td>
                <td className="space-x-2 px-2 py-1 text-right">
                  {item.threadId === null ? null : <Link to={appPaths.thread(item.threadId)} className="app-link underline">{t("batches.openThread")}</Link>}
                  {isStarter && item.state === "failed" && batch.state !== "cancelled" ? (
                    <AppButton variant="secondary" size="sm" disabled={retry.state.status === "submitting"} onClick={() => { retryItem(item); }}>{t("batches.retry")}</AppButton>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {state.status === "loaded" ? (
        <div className="flex justify-end gap-2">
          <AppButton variant="ghost" size="sm" disabled={pageStarts.length === 1} onClick={() => { setPageStarts(pageStarts.slice(0, -1)); }}>{t("batches.previousPage")}</AppButton>
          <AppButton
            variant="ghost"
            size="sm"
            disabled={state.value.length < BATCH_ITEMS_PAGE}
            onClick={() => {
              const last = state.value[state.value.length - 1];
              if (last !== undefined) setPageStarts([...pageStarts, last.index + 1]);
            }}
          >
            {t("batches.nextPage")}
          </AppButton>
        </div>
      ) : null}
    </section>
  );
}

function usageText(usage: Usage, unknown: string): string {
  const tokens = usage.inputTokens === null && usage.outputTokens === null ? unknown : `${String(usage.inputTokens ?? 0)} → ${String(usage.outputTokens ?? 0)} tokens`;
  const costs = Object.entries(usage.cost).map(([currency, amount]) => `${amount.toFixed(4)} ${currency}`);
  return [tokens, ...costs].join(" · ");
}

function durationText(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return minutes === 0 ? `${String(Math.round(seconds))} s` : `${String(minutes)} min ${String(Math.round(seconds % 60))} s`;
}
